import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react";
import { Day, SchedulerData, SchedulerProjectData, TooltipData, ZoomLevel } from "../../types/global";
import { Grid, Header, LeftColumn, Tooltip, Topbar } from "..";
import { StyledEmptyBoxWrapper, StyledInnerWrapper, StyledOuterWrapper } from "./styles";
import { Theme, useMediaQuery } from "@mui/material";

import { CalendarProps } from "./types";
import EmptyBox from "../EmptyBox";
import debounce from "lodash.debounce";
import { getTooltipData } from "../../utils/getTooltipData";
import { useCalendar } from "../../context/CalendarProvider";
import { usePagination } from "../../hooks/usePagination";

const initialTooltipData: TooltipData = {
  coords: { x: 0, y: 0 },
  resourceIndex: 0,
  disposition: {
    taken: { hours: 0, minutes: 0 },
    free: { hours: 0, minutes: 0 },
    overtime: { hours: 0, minutes: 0 }
  }
};

export const Calendar: FC<CalendarProps> = ({
  data,
  onTileClick,
  onItemClick,
  onTooltipOpen,
  onVehicleClick,
  onClickNewRequest,
  onVehicleMapDetailClick,
  onItemDrop,
  toggleTheme,
  onItemResize,
  topBarWidth,
  onItemCreate
}) => {
  const [tooltipData, setTooltipData] = useState<TooltipData>(initialTooltipData);
  const [filteredData, setFilteredData] = useState(data);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const {
    zoom,
    startDate,
    config: { includeTakenHoursOnWeekendsInDayView, showTooltip, showThemeToggle }
  } = useCalendar();
  const gridRef = useRef<HTMLDivElement>(null);
  const {
    page,
    projectsPerPerson,
    totalRowsPerPage,
    rowsPerItem,
    currentPageNum,
    pagesAmount,
    next,
    previous,
    reset
  } = usePagination(filteredData);
  const debouncedHandleMouseOver = useRef(
    debounce(
      (
        e: MouseEvent,
        startDate: Day,
        rowsPerItem: number[],
        projectsPerPerson: SchedulerProjectData[][][],
        zoom: ZoomLevel
      ) => {
        if (!gridRef.current) return;
        const { left, top } = gridRef.current.getBoundingClientRect();
        const tooltipCoords = { x: e.clientX - left, y: e.clientY - top };
        const {
          coords: { x, y },
          resourceIndex,
          disposition
        } = getTooltipData(
          startDate,
          tooltipCoords,
          rowsPerItem,
          projectsPerPerson,
          zoom,
          includeTakenHoursOnWeekendsInDayView
        );
        setTooltipData({ coords: { x, y }, resourceIndex, disposition });
        setIsVisible(true);
      },
      300
    )
  );
  const debouncedFilterData = useRef(
    debounce((dataToFilter: SchedulerData, enteredSearchPhrase: string) => {
      reset();
      setFilteredData(
        dataToFilter.filter((item) =>
          item.label.title.toLowerCase().includes(enteredSearchPhrase.toLowerCase())
        )
      );
    }, 500)
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const phrase = event.target.value;
    setSearchPhrase(phrase);
    debouncedFilterData.current.cancel();
    debouncedFilterData.current(data, phrase);
  };

  const handleMouseLeave = useCallback(() => {
    debouncedHandleMouseOver.current.cancel();
    setIsVisible(false);
    setTooltipData(initialTooltipData);
  }, []);

  useEffect(() => {
    const handleMouseOver = (e: MouseEvent) =>
      debouncedHandleMouseOver.current(e, startDate, rowsPerItem, projectsPerPerson, zoom);
    const gridArea = gridRef.current;

    if (!gridArea) return;

    gridArea.addEventListener("mousemove", handleMouseOver);
    gridArea.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      gridArea.removeEventListener("mousemove", handleMouseOver);
      gridArea.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [debouncedHandleMouseOver, handleMouseLeave, projectsPerPerson, rowsPerItem, startDate, zoom]);

  useEffect(() => {
    // if (searchPhrase) return;

    setFilteredData(data);
  }, [data, searchPhrase]);

  const onFocusTile = (row: SchedulerProjectData) => {
    setTooltipItem(row);
    onTooltipOpen?.(row);
  }

  return (
    <>
      <StyledOuterWrapper>
        <LeftColumn
          data={page}
          pageNum={currentPageNum}
          pagesAmount={pagesAmount}
          rows={rowsPerItem}
          onLoadNext={next}
          onLoadPrevious={previous}
          searchInputValue={searchPhrase}
          onSearchInputChange={handleSearch}
          onItemClick={onItemClick}
          onVehicleClick={onVehicleClick}
          onVehicleMapDetailClick={onVehicleMapDetailClick}
        />
        <StyledInnerWrapper>
          <Header
            zoom={zoom}
            topBarWidth={topBarWidth}
            showThemeToggle={showThemeToggle}
            toggleTheme={toggleTheme}
          />
          {data.length ? (
            <Grid
              data={page}
              zoom={zoom}
              rows={totalRowsPerPage}
              rowsPerItem={rowsPerItem}
              ref={gridRef}
              onFocusTile={onFocusTile}
              onTileClick={onTileClick}
              onItemDrop={onItemDrop}
              onItemResize={onItemResize}
              onItemCreate={onItemCreate}
            />
          ) : (
            <StyledEmptyBoxWrapper width={topBarWidth}>
              <EmptyBox />
            </StyledEmptyBoxWrapper>
          )}
          {showTooltip && tooltipItem && isVisible && tooltipData?.resourceIndex > -1 && (
            <Tooltip tooltipData={tooltipData} tooltipItem={tooltipItem} zoom={zoom} />
          )}
        </StyledInnerWrapper>
      </StyledOuterWrapper>
    </>
  );
};

export default Calendar;
